export type ServiceBillingProposalStatus = 'draft' | 'ready' | 'returned' | 'invoiced';
export type ServiceBillingLineKind = 'labour' | 'travel-time' | 'kilometres' | 'article' | 'service' | 'text';

export interface ServiceBillingLine {
  readonly id: string;
  readonly kind: ServiceBillingLineKind;
  readonly sourceNumber: string;
  readonly description: string;
  readonly quantity: number;
  readonly unit: string;
  readonly unitPrice: number;
  readonly discountPercent: number;
  readonly vatRate: number;
  readonly netAmount: number;
}

export interface ServiceBillingProposal {
  readonly id: string;
  readonly tenantId: string;
  readonly serviceJobId: string;
  readonly serviceNumber: string;
  readonly orderId: string;
  readonly orderNumber: string;
  readonly customerId: string | null;
  readonly customerNumber: string;
  readonly customerName: string;
  readonly invoiceAddress: string;
  readonly machineLabels: readonly string[];
  readonly serviceDate: string;
  readonly technicianNames: readonly string[];
  readonly workPerformed: string;
  readonly findings: string;
  readonly paperPrintDocumentId: string | null;
  readonly customerSignatureOnPaper: boolean;
  readonly lines: readonly ServiceBillingLine[];
  readonly netTotal: number;
  readonly vatTotal: number;
  readonly grossTotal: number;
  readonly status: ServiceBillingProposalStatus;
  readonly version: number;
  readonly preparedBy: string;
  readonly preparedAt: string;
  readonly returnedReason: string;
  readonly returnedAt: string | null;
  readonly invoicedNumber: string;
}

export interface SaveServiceBillingProposalInput {
  readonly tenantId: string;
  readonly serviceJobId: string;
  readonly serviceNumber: string;
  readonly orderId: string;
  readonly orderNumber: string;
  readonly customerId?: string | null;
  readonly customerNumber?: string;
  readonly customerName: string;
  readonly invoiceAddress: string;
  readonly machineLabels: readonly string[];
  readonly serviceDate: string;
  readonly technicianNames: readonly string[];
  readonly workPerformed: string;
  readonly findings?: string;
  readonly paperPrintDocumentId?: string | null;
  readonly customerSignatureOnPaper: boolean;
  readonly lines: readonly Omit<ServiceBillingLine, 'id' | 'netAmount'>[];
  readonly preparedBy: string;
}

export interface ReturnServiceBillingProposalInput {
  readonly tenantId: string;
  readonly proposalId: string;
  readonly reason: string;
}

export interface MarkServiceBillingProposalInvoicedInput {
  readonly tenantId: string;
  readonly proposalId: string;
  readonly invoiceNumber: string;
}

export interface ServiceBillingGateway {
  listProposals(tenantId: string, status?: ServiceBillingProposalStatus | 'all'): Promise<readonly ServiceBillingProposal[]>;
  saveAndSubmit(input: SaveServiceBillingProposalInput): Promise<ServiceBillingProposal>;
  returnToService(input: ReturnServiceBillingProposalInput): Promise<ServiceBillingProposal>;
  markInvoiced(input: MarkServiceBillingProposalInvoicedInput): Promise<ServiceBillingProposal>;
}

export function calculateServiceBillingLine(line: Omit<ServiceBillingLine, 'id' | 'netAmount'>): number {
  const quantity = Math.max(0, Number(line.quantity) || 0);
  const unitPrice = Math.max(0, Number(line.unitPrice) || 0);
  const discount = Math.min(100, Math.max(0, Number(line.discountPercent) || 0));
  return Math.round(quantity * unitPrice * (1 - discount / 100) * 100) / 100;
}

export function calculateServiceBillingTotals(lines: readonly ServiceBillingLine[]) {
  const netTotal = Math.round(lines.reduce((sum, line) => sum + line.netAmount, 0) * 100) / 100;
  const vatTotal = Math.round(lines.reduce((sum, line) => sum + line.netAmount * line.vatRate / 100, 0) * 100) / 100;
  return { netTotal, vatTotal, grossTotal: Math.round((netTotal + vatTotal) * 100) / 100 };
}

export function validateServiceBillingProposal(input: SaveServiceBillingProposalInput): void {
  if (!input.tenantId.trim()) throw new Error('Mandant fehlt.');
  if (!input.serviceJobId.trim()) throw new Error('Serviceauftrag fehlt.');
  if (!input.customerName.trim()) throw new Error('Kunde fehlt.');
  if (!input.serviceDate.trim()) throw new Error('Leistungsdatum fehlt.');
  if (!input.workPerformed.trim()) throw new Error('Ausgeführte Arbeiten fehlen.');
  if (!input.preparedBy.trim()) throw new Error('Service-Freigabe fehlt.');
  if (input.lines.length === 0) throw new Error('Mindestens eine abrechenbare Position ist erforderlich.');
  if (input.lines.some((line) => !line.description.trim() || line.quantity <= 0)) throw new Error('Abrechnungspositionen sind unvollständig.');
}

export { createDemoServiceBillingGateway, DemoServiceBillingGateway } from './demoGateway';
